import '../scss/components/_ownerrevenue.scss'
import React, { useState, useEffect } from 'react'
import { getPGRevenueStatus, autoGenerateRent, process_rent_payment, autoGenerateElectricityBill, getTenantStatement } from '../apis/services/pgApi'

const STATUS_CFG = {
  PAID:        { label: 'Paid',       dot: 'green', bg: '#F0FDF4', border: 'green', text: 'green' },
  DUE:         { label: 'Due',        dot: 'blue', bg: '#FEFCE8', border: 'blue', text: 'blue' },
  OVERDUE:     { label: 'Overdue',    dot: 'orange', bg: '#FFF7ED', border: 'orange', text: 'orange' },
  "NOT PAID":  { label: 'Not Paid',   dot: 'red', bg: '#FFF1F2', border: 'red', text: 'red' },
  VACANT:      { label: 'Vacant',     dot: 'gray', bg: '#F9FAFB', border: 'gray', text: 'gray' },
}

const RENT_STATUS_CFG = {
  PAID:     { label: 'Paid',     dot: 'green', bg: '#F0FDF4', border: 'green', text: 'green' },
  DUE:      { label: 'Due',      dot: 'blue', bg: '#FEFCE8', border: 'blue', text: 'blue' },
  OVERDUE:  { label: 'Overdue',  dot: 'orange', bg: '#FFF7ED', border: 'orange', text: 'orange' },
  "NOT PAID":  { label: 'Not Paid',  dot: 'red', bg: '#FFF1F2', border: 'red', text: 'red' },
  NA:       { label: 'N/A',      dot: 'gray', bg: '#F9FAFB', border: 'gray', text: 'gray' },
}

const fmt = n => `₹${(n || 0).toLocaleString('en-IN')}`

const STATUS_FILTERS = [
  { key: 'PAID', label: 'Paid' ,bg: 'green'},
  { key: 'DUE', label: 'Due' ,bg: 'blue'},
  { key: 'OVERDUE', label: 'Overdue' ,bg: 'orange'},
  { key: 'NOT PAID', label: 'Not Paid' ,bg: 'red'},
]

const getStatusColor = (status) => {
  switch(status) {
    case 'PAID':
      return { bg: '#DCFCE7', color: '#166534', dot: '#22C55E' }
    case 'DUE':
      return { bg: '#fefce8', color: '#1b2d80', dot: '#1e49c0' }
    case 'OVERDUE':
      return { bg: '#FFEDD5', color: '#9A3412', dot: '#F97316' }
    case 'NOT PAID':
      return { bg: '#FEE2E2', color: '#991B1B', dot: '#EF4444' }
    default:
      return { bg: '#F3F4F6', color: '#6B7280', dot: '#9CA3AF' }
  }
}

// ─── RentPaymentModal (with Send Reminder button) ────────────────
const RentPaymentModal = ({ pgId, bed, onClose, onSuccess, onSendReminder }) => {
  const [paymentMode, setPaymentMode] = useState('CASH')
  const [netPayment, setNetPayment] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState('')
  const [tenantStatement, setTenantStatement] = useState([])
  const [isLoadingStatement, setIsLoadingStatement] = useState(false)

  const statusColors = getStatusColor(bed?.rentStatus)
  const showReminder = ['DUE', 'OVERDUE', 'NOT PAID'].includes(bed?.rentStatus)

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  useEffect(() => {
    const fetchTenantStatement = async () => {
      if (bed?.admissionId && pgId) {
        setIsLoadingStatement(true)
        try {
          const response = await getTenantStatement(bed.admissionId, pgId)
          setTenantStatement(response || [])
        } catch (err) {
          console.error('Error fetching tenant statement:', err)
          setTenantStatement([])
        } finally {
          setIsLoadingStatement(false)
        }
      }
    }
    fetchTenantStatement()
  }, [bed?.admissionId, pgId])

  if (!bed) return null

  const handleSubmit = async () => {
    if (!netPayment || netPayment.trim() === '') {
      setError('Please enter net payment amount')
      return
    }
    const paymentAmount = parseInt(netPayment)
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      setError('Please enter a valid payment amount')
      return
    }
    if (!bed?.admissionId) {
      setError('No admission ID found')
      return
    }

    setIsProcessing(true)
    setError('')

    try {
      const paymentData = {
        AdmissionID: bed.admissionId,
        PaymentAmount: paymentAmount,
        PaymentMode: paymentMode,
        DueinMonth: bed.RentdueInMonth
      }
      const response = await process_rent_payment(paymentData)
      if (response.success) {
        onSuccess(bed.bedId, 'PAID')
        onClose()
      } else {
        setError(response.message || 'Payment failed')
      }
    } catch (err) {
      setError(err.message || 'An error occurred')
    } finally {
      setIsProcessing(false)
    }
  }

  const maxPayable = bed.NetPayable || bed.rent || 0

  const handleReminderClick = () => {
    if (onSendReminder) {
      onSendReminder(bed)
    }
    onClose() // close modal after sending reminder
  }

  return (
    <div className="rv-modal-overlay">
      <div className="rv-rent-popover rv-modal-compact" onClick={e => e.stopPropagation()}>
        <div className="rv-rp-head">
          <span className="rv-rp-name">{bed.name || 'Tenant'}</span>
          <button className="rv-rp-close" onClick={onClose}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="rv-rp-status-due-row">
          <div className="rv-rp-status">
            <span className="rv-rp-label">Status:</span>
            <span className="rv-rp-badge" style={{ background: statusColors.bg, color: statusColors.color }}>
              <span className="rv-rp-dot" style={{ background: statusColors.dot }} />
              {bed.rentStatus === 'NOT PAID' ? 'NOT PAID' : (bed.rentStatus || 'NA')}
            </span>
          </div>
          <div className="rv-rp-reminder-wrapper">
            {showReminder && (
              <button
                className="rv-rp-reminder-btn"
                onClick={handleReminderClick}
                title="Send WhatsApp reminder"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 01-3.46 0" />
                </svg>
                Send Reminder
              </button>
            )}
          </div>
        </div>

        {isLoadingStatement ? (
          <div className="rv-statement-loading">
            <span className="rv-spinner-small"></span> Loading statement...
          </div>
        ) : tenantStatement.length > 0 ? (
          <div className="rv-tenant-statement">
            <div className="rv-statement-title">Monthly Statement</div>
            <div className="rv-statement-table-wrapper">
              <table className="rv-statement-table">
                <thead>
                  <tr>
                    <th>Month</th><th>Year</th><th>Rent (₹)</th>
                    <th>Electricity (₹)</th><th>Total (₹)</th>
                    <th>Paid (₹)</th><th>Net Payable (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {tenantStatement.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.MonthName}</td>
                      <td>{item.InvoiceYear}</td>
                      <td>{(item.RentAmount || 0).toLocaleString('en-IN')}</td>
                      <td>{(item.ElectricityAmount || 0).toLocaleString('en-IN')}</td>
                      <td>{(item.TotalAmount || 0).toLocaleString('en-IN')}</td>
                      <td className="rv-paid-amount">{(item.PaidAmount || 0).toLocaleString('en-IN')}</td>
                      <td className="rv-netpayable-amount">{(item.NetPayable || 0).toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        <div className="rv-rp-amount-grid">
          <div className="rv-rp-amount-item rv-rp-netpayable-item">
            <span className="rv-rp-label">Total Net Payable:</span>
            <span className="rv-rp-value">{fmt(maxPayable)}</span>
          </div>
        </div>

        <div className="rv-rp-netpayment">
          <label className="rv-rp-netpayment-label">
            Net Payment <span className="rv-required">*</span>
          </label>
          <div className="rv-rp-netpayment-input-wrap">
            <span className="rv-rp-currency-symbol">₹</span>
            <input
              type="number"
              className="rv-rp-netpayment-input"
              placeholder="Enter payment amount"
              value={netPayment}
              onChange={(e) => setNetPayment(e.target.value)}
              max={maxPayable}
              min="1"
              step="1"
              required
            />
          </div>
        </div>

        <div className="rv-rp-payment-mode">
          <label className="rv-rp-payment-label">Payment Mode:</label>
          <select
            value={paymentMode}
            onChange={(e) => setPaymentMode(e.target.value)}
            className="rv-rp-payment-select"
          >
            <option value="CASH">💵 CASH</option>
            <option value="UPI">📱 UPI</option>
            <option value="BANK">🏦 BANK</option>
          </select>
        </div>

        {error && <div className="rv-rp-error">{error}</div>}

        <button
          className="rv-rp-mark-btn"
          onClick={handleSubmit}
          disabled={isProcessing}
        >
          {isProcessing ? 'Processing...' : 'Confirm Payment'}
        </button>
      </div>
    </div>
  )
}

// ─── OwnerRevenue main component ──────────────────────────────────
const OwnerRevenue = ({ selectedPG, selectedPGId }) => {
  const [floor, setFloor] = useState('all')
  const [selectedStatuses, setSelectedStatuses] = useState([])
  const [search, setSearch] = useState('')
  const [rooms, setRooms] = useState([])
  const [totals, setTotals] = useState({ total: 0, collected: 0, due: 0, overdue: 0, notPaid: 0 })
  const [floors, setFloors] = useState(['all'])
  const [localRentStatus, setLocalRentStatus] = useState({})
  const [selectedBed, setSelectedBed] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)


  
  
  // const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0])
 const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    // Add 5 hours 30 minutes
    now.setHours(now.getHours() + 5);
    now.setMinutes(now.getMinutes() + 30);
    return now.toISOString().slice(0, 16); // "2026-06-22T21:00"
  });
  const [isGenerating, setIsGenerating] = useState(false)
  const [generateMessage, setGenerateMessage] = useState(null)
  const [isGeneratingElectricity, setIsGeneratingElectricity] = useState(false)
  const [electricityMessage, setElectricityMessage] = useState(null)

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' })

  const handleSendReminder = async (bed) => {
    console.log('Sending reminder for:', bed.name)
    // TODO: Replace with actual API call
    setToast({
      show: true,
      message: `WhatsApp reminder sent to ${bed.name || 'tenant'}!`,
      type: 'success'
    })
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000)
  }

  const floorOrder = {
    'Ground Floor': 0,
    '1st Floor': 1,
    '2nd Floor': 2,
    '3rd Floor': 3,
    '4th Floor': 4,
    '5th Floor': 5
  }

  const fetchRevenueData = async () => {
    try {
      const response = await getPGRevenueStatus(selectedPGId, selectedDate)
      const roomsMap = new Map()
      response.occupancyData.forEach(item => {
        const roomId = item.RoomID
        const floorStr = item.Floor || 'Unknown'
        if (!roomsMap.has(roomId)) {
          roomsMap.set(roomId, {
            id: item.RoomNumber,
            floor: floorStr,
            type: item.RoomType?.toLowerCase() || 'unknown',
            beds: []
          })
        }
        const room = roomsMap.get(roomId)
        const statusValue = item.status || item.Status || 'VACANT'
        const occupancyStatus = item.OccupancyStatus === 'VACANT' ? 'VACANT' : statusValue
        room.beds.push({
          bedId: item.BedID,
          name: item.TenantName || '',
          status: occupancyStatus || 'VACANT',
          rent: item.NetPayable || 0,
          rentStatus: statusValue || 'NA',
          admissionId: item.AdmissionID || item.admissionId,
          RentAmount: item.RentAmount,
          ElectricityAmount: item.ElectricityAmount,
          TotalAmount: item.TotalAmount,
          NetPayable: item.NetPayable,
          RentdueInMonth: item.RentDueInMonth || item.RentdueInMonth,
          DueDate: item.DueDate
        })
      })

      const roomsData = Array.from(roomsMap.values()).sort((a, b) => {
        const aFloorOrder = floorOrder[a.floor] !== undefined ? floorOrder[a.floor] : 999
        const bFloorOrder = floorOrder[b.floor] !== undefined ? floorOrder[b.floor] : 999
        if (aFloorOrder !== bFloorOrder) return aFloorOrder - bFloorOrder
        const aNum = parseInt(a.id), bNum = parseInt(b.id)
        if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum
        return String(a.id).localeCompare(String(b.id))
      })
      setRooms(roomsData)

      const uniqueFloorsSet = new Set(roomsData.map(r => r.floor))
      const uniqueFloors = ['all', ...Array.from(uniqueFloorsSet).sort((a, b) => {
        const aOrder = floorOrder[a] !== undefined ? floorOrder[a] : 999
        const bOrder = floorOrder[b] !== undefined ? floorOrder[b] : 999
        return aOrder - bOrder
      })]
      setFloors(uniqueFloors)

      if (response.totalsArray && response.totalsArray[0]) {
        setTotals({
          total: response.totalsArray[0].Total || 0,
          collected: response.totalsArray[0].Collected || 0,
          due: response.totalsArray[0].Due || 0,
          overdue: response.totalsArray[0].Overdue || 0,
          notPaid: response.totalsArray[0].NotPaid || 0,
        })
      }
    } catch (error) {
      console.error('Error fetching revenue data:', error)
    }
  }

  useEffect(() => {
    if (selectedPGId) fetchRevenueData()
  }, [selectedPGId, selectedDate])

  // ✅ SQL DateTime Format - For API
  const formatSQLDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    
    const date = new Date(dateTimeString);
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  const formatDisplayDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    const date = new Date(dateTimeString);
    
    return date.toLocaleString('en-IN', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }



  const handleAutoGenerate = async () => {
    if (!selectedPGId) {
      setGenerateMessage({ type: 'error', text: 'No PG selected' })
      setTimeout(() => setGenerateMessage(null), 3000)
      return
    }
    setIsGenerating(true)
    setGenerateMessage(null)
    try {
      const formattedDateTime = formatSQLDateTime(selectedDate);
      const data = { PGID: selectedPGId, CurrDate: formattedDateTime }
      console.log('Auto-generate rent with data:', data)
      const response = await autoGenerateRent(data)
      if (response.success) {
        setGenerateMessage({ type: 'success', text: `Rent generated successfully! ${response.data?.affectedRecords || 0} records affected.` })
        await fetchRevenueData()
      } else {
        setGenerateMessage({ type: 'error', text: response.message || 'Failed to generate rent' })
      }
    } catch (error) {
      console.error('Error auto-generating rent:', error)
      setGenerateMessage({ type: 'error', text: error.message || 'An error occurred' })
    } finally {
      setIsGenerating(false)
      setTimeout(() => setGenerateMessage(null), 5000)
    }
  }

  const handleAutoGenerateElectricity = async () => {
    if (!selectedPGId) {
      setElectricityMessage({ type: 'error', text: 'No PG selected' })
      setTimeout(() => setElectricityMessage(null), 3000)
      return
    }
    const date = new Date(selectedDate)
    const month = date.getMonth() + 1
    const year = date.getFullYear()
    setIsGeneratingElectricity(true)
    setElectricityMessage(null)
    try {
      
      const data = { PGID: selectedPGId, Month: month, Year: year }
      console.log('Auto-generate electricity bill with data:', data)
      const response = await autoGenerateElectricityBill(data)
      if (response.success) {
        setElectricityMessage({ type: 'success', text: `Electricity bill generated successfully! ${response.data?.affectedRecords || 0} records affected.` })
        await fetchRevenueData()
      } else {
        setElectricityMessage({ type: 'error', text: response.message || 'Failed to generate electricity bill' })
      }
    } catch (error) {
      console.error('Error auto-generating electricity bill:', error)
      setElectricityMessage({ type: 'error', text: error.message || 'An error occurred' })
    } finally {
      setIsGeneratingElectricity(false)
      setTimeout(() => setElectricityMessage(null), 5000)
    }
  }

  const handleMarkRent = async (bedId, newStatus) => {
    setLocalRentStatus(prev => ({ ...prev, [bedId]: newStatus }))
    await fetchRevenueData()
  }

  const toggleStatusFilter = (statusKey) => {
    setSelectedStatuses(prev =>
      prev.includes(statusKey) ? prev.filter(s => s !== statusKey) : [...prev, statusKey]
    )
  }

  const filtered = rooms
    .map(room => {
      const filteredBeds = room.beds.filter(bed => {
        const matchesSearch = !search ||
          bed.name?.toLowerCase().includes(search.toLowerCase()) ||
          `room ${room.id}`.toLowerCase().includes(search.toLowerCase())
        let matchesStatus = true
        if (selectedStatuses.length > 0) {
          const bedStatus = localRentStatus[bed.bedId] || bed.rentStatus
          matchesStatus = selectedStatuses.includes(bedStatus)
        }
        return matchesSearch && matchesStatus
      })
      const matchesFloor = floor === 'all' || room.floor === floor
      if (matchesFloor && filteredBeds.length > 0) {
        return { ...room, beds: filteredBeds }
      }
      return null
    })
    .filter(room => room !== null)

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
  }

  return (
    <div className="rv-root">
      {toast.show && (
        <div className={`rv-toast rv-toast-${toast.type}`}>
          <span className="rv-toast-icon">✓</span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* <div className="rv-auto-generate-wrapper">
        <div className="rv-date-control">
          <label className="rv-date-label">Select Date:</label>
          <input
            type="datetime-local"
            className="rv-date-input"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
         <span className="rv-selected-date">
             Selected: {selectedDate ? formatDisplayDateTime(selectedDate) : 'N/A'}
          </span>
        </div>
        <div className="rv-action-buttons">
          <button
            className="rv-auto-gen-btn"
            onClick={handleAutoGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <><span className="rv-spinner"></span> Generating Rent...</>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9h-4m-7 9A9 9 0 013 12m9 9v-4M3 12a9 9 0 019-9m-9 9h4m7-9a9 9 0 00-9 9m9-9v4" />
                </svg>
                Auto Generate Rent
              </>
            )}
          </button>
          <button
            className="rv-auto-gen-btn rv-electricity-btn"
            onClick={handleAutoGenerateElectricity}
            disabled={isGeneratingElectricity}
          >
            {isGeneratingElectricity ? (
              <><span className="rv-spinner"></span> Generating Bill...</>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  <circle cx="12" cy="12" r="4" />
                </svg>
                Generate Electricity Bill
              </>
            )}
          </button>
        </div>
      </div> */}

      {generateMessage && (
        <div className={`rv-message rv-message-${generateMessage.type}`}>
          {generateMessage.text}
        </div>
      )}
      {electricityMessage && (
        <div className={`rv-message rv-message-${electricityMessage.type}`}>
          {electricityMessage.text}
        </div>
      )}

      <div className="rv-summary">
        <div className="rv-legend">
          {['PAID','DUE','OVERDUE','NOT PAID'].map(k => (
            <span key={k} className="rv-legend-item">
              <span className="rv-dot" style={{ background: STATUS_CFG[k].dot }} />
              {STATUS_CFG[k].label}
            </span>
          ))}
        </div>
        <div className="rv-stats">
          <span>Total: <strong>{fmt(totals.total)}</strong></span>
          <span className="rv-stat-sep" />
          <span>Collected: <strong className="rv-green-1">{fmt(totals.collected)}</strong></span>
          <span className="rv-stat-sep" />
          <span>Due: <strong className="rv-blue-1">{fmt(totals.due)}</strong></span>
          <span className="rv-stat-sep" />
          <span>Overdue: <strong className="rv-orange-1">{fmt(totals.overdue)}</strong></span>
          <span className="rv-stat-sep" />
          <span>Not Paid: <strong className="rv-red-1">{fmt(totals.notPaid)}</strong></span>
        </div>
      </div>

      <div className="rv-controls">
        <div className="rv-search-wrap">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input className="rv-search" placeholder="Search room or tenant…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="rv-filter-group">
          {floors.map(f => (
            <button key={f} className={`rv-floor-pill ${floor === f ? 'active' : ''}`} onClick={() => setFloor(f)}>
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
        <div className="rv-filter-group">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.key}
              className={`rv-status-pill`}
              style={{
                background: selectedStatuses.includes(f.key) ? f.bg : '#F3F4F6',
                color: selectedStatuses.includes(f.key) ? 'white' : '#4B5563',
                borderColor: selectedStatuses.includes(f.key) ? f.bg : '#E5E7EB',
                transition: 'all 0.2s ease'
              }}
              onClick={() => toggleStatusFilter(f.key)}
            >
              {f.label}
              {selectedStatuses.includes(f.key) && <span className="rv-checkmark"> ✓</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="rv-grid">
        {filtered.map(room => (
          <div key={room.id} className="rv-card">
            <div className="rv-card-head">
              <div className="rv-card-head-left">
                <span className="rv-card-room">Room {room.id}</span>
                <div className="rv-card-badges">
                  <span className="rv-card-floor">{room.floor}</span>
                  <span className="rv-card-type">{room.type}</span>
                </div>
              </div>
            </div>

            <div className="rv-pill-grid">
              {room.beds.map((bed, i) => {
                const statusCfg = STATUS_CFG[bed.status] || STATUS_CFG.VACANT
                const effectiveRentStatus = localRentStatus[bed.bedId] || bed.rentStatus
                const isClickable = effectiveRentStatus !== 'PAID' && bed.status !== 'VACANT'

                return (
                  <div key={i} className="rv-pill-wrap">
                    <div
                      className={`rv-pill ${isClickable ? 'rv-pill-clickable' : ''}`}
                      style={{ background: statusCfg.bg, borderColor: statusCfg.border }}
                      onClick={() => {
                        if (isClickable) {
                          setSelectedBed(bed)
                          setIsModalOpen(true)
                        }
                      }}
                    >
                      <span className="rv-dot" style={{ background: statusCfg.dot }} />
                      <span className="rv-pill-name" style={{ color: statusCfg.text }}>
                        {bed.name || statusCfg.label}
                      </span>
                      {/* Rent amount removed as requested */}
                    </div>
                  </div>
                )
              })}
              {room.beds.length < 4 && [...Array(4 - room.beds.length)].map((_, i) => (
                <div key={`empty-${i}`} className="rv-pill rv-pill-ghost" />
              ))}
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="rv-no-results">No tenants match your search criteria</div>
      )}

      {isModalOpen && (
        <RentPaymentModal
          pgId={selectedPGId}
          bed={selectedBed}
          onClose={() => { setIsModalOpen(false); setSelectedBed(null); }}
          onSuccess={handleMarkRent}
          onSendReminder={handleSendReminder}
        />
      )}
    </div>
  )
}

export default OwnerRevenue